import { api } from "./api";
import type {
  CreateSessionRequest,
  CreateSessionResponse,
} from "@habitify/shared/api";

/**
 * Each variation gets its own session in storage so a visitor flipping
 * between funnel slugs shows up as two funnel entries (which is what
 * the marketer wants — they're testing the variations, not the user).
 */
const KEY = (slug: string) => `h2a:session:${slug}`;

export function getSessionId(slug: string): string | null {
  try {
    return sessionStorage.getItem(KEY(slug));
  } catch {
    return null;
  }
}

function setSessionId(slug: string, id: string) {
  try {
    sessionStorage.setItem(KEY(slug), id);
  } catch {
    /* private mode — fall back to in-memory only */
  }
}

/** First-touch UTMs persist across the survey even after URL changes. */
function captureUtm(): Record<string, string> {
  const u = new URLSearchParams(window.location.search);
  const out: Record<string, string> = {};
  for (const k of [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
  ] as const) {
    const v = u.get(k);
    if (v) out[k] = v;
  }
  return out;
}

function getCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return m && m[1] ? decodeURIComponent(m[1]) : null;
}

/**
 * In-flight session mints, keyed by slug. We share the same promise
 * across concurrent callers so the very common race below collapses
 * into a single network round-trip:
 *
 *   • VariationApp's mount effect: `void ensureSession(slug)`
 *   • The orchestrator's first `trackView` (its `flush()` also awaits
 *     ensureSession before POSTing the event).
 *
 * Without this memo, both callers see empty sessionStorage at the
 * same instant, both POST /api/sessions, and the worker mints TWO
 * sessions for the same user — half the dashboard's session count
 * is ghost rows with just a `session_start` event, and events queued
 * by the loser flush can land on the orphan id. Production data
 * before this fix showed a duplicate session for every real visitor.
 */
const inflight = new Map<string, Promise<string>>();

export async function ensureSession(slug: string): Promise<string> {
  const existing = getSessionId(slug);
  if (existing) return existing;

  const pending = inflight.get(slug);
  if (pending) return pending;

  const body: CreateSessionRequest = {
    variationSlug: slug,
    fbp: getCookie("_fbp"),
    fbc: getCookie("_fbc"),
    utm: captureUtm(),
    referrer: document.referrer || null,
    landingPath: window.location.pathname + window.location.search,
  };

  const p = (async () => {
    try {
      const res = await api.post<CreateSessionResponse>("/api/sessions", body);
      setSessionId(slug, res.sessionId);
      return res.sessionId;
    } finally {
      // Always clear so a subsequent failure → retry path can mint
      // again. Successful mints are subsequently short-circuited by
      // the sessionStorage check at the top of the function.
      inflight.delete(slug);
    }
  })();

  inflight.set(slug, p);
  return p;
}
